/**
 * 🏠 USER DASHBOARD - Customer-facing dashboard with verification flow
 * Implements the complete business logic: Signup → Renter → Verification → Verified User
 * Shows locked/unlocked features based on verification status
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Alert,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Badge,
} from '@mui/material';
import {
  VerifiedUser,
  Lock,
  DirectionsCar,
  Person,
  Warning,
  CheckCircle,
  Schedule,
  Assignment,
  Notifications,
  Settings,
  Help,
  TrendingUp,
  MonetizationOn,
  Security,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { User, UserRole, VerificationStatus } from '@/types';

interface DashboardSection {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  isLocked: boolean;
  lockReason?: string;
  action: () => void;
  buttonText: string;
  buttonColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'success';
}

export const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [driverApplicationOpen, setDriverApplicationOpen] = useState(false);
  
  if (!user) {
    navigate('/auth/login');
    return null;
  }

  const isUnverified = !user.isVerified;
  const isVerificationPending = user.verificationStatus === VerificationStatus.PENDING_REVIEW;
  const isVerificationRejected = user.verificationStatus === VerificationStatus.REJECTED;
  
  // Dashboard sections based on user permissions
  const dashboardSections: DashboardSection[] = [
    {
      id: 'rent-cars',
      title: '🚗 Rent Vehicles',
      description: 'Browse and book available vehicles',
      icon: <DirectionsCar sx={{ fontSize: 40 }} />,
      isLocked: !user.canRentCars,
      lockReason: 'Complete verification to rent vehicles',
      action: () => user.canRentCars ? navigate('/vehicles') : setVerificationDialogOpen(true),
      buttonText: user.canRentCars ? 'Browse Vehicles' : 'Unlock Rentals',
      buttonColor: user.canRentCars ? 'primary' : 'warning'
    },
    {
      id: 'hire-drivers',
      title: '👨‍✈️ Hire Drivers', 
      description: 'Find and hire professional drivers',
      icon: <Person sx={{ fontSize: 40 }} />,
      isLocked: !user.canHireDrivers,
      lockReason: 'Complete verification to hire drivers',
      action: () => user.canHireDrivers ? navigate('/drivers') : setVerificationDialogOpen(true),
      buttonText: user.canHireDrivers ? 'Find Drivers' : 'Unlock Driver Hire',
      buttonColor: user.canHireDrivers ? 'primary' : 'warning'
    },
    {
      id: 'become-driver',
      title: '🚙 Become a Driver',
      description: 'Earn money by driving for others',
      icon: <MonetizationOn sx={{ fontSize: 40 }} />,
      isLocked: false,
      action: () => setDriverApplicationOpen(true),
      buttonText: user.role === UserRole.DRIVER ? 'Driver Dashboard' : 'Apply as Driver',
      buttonColor: 'secondary'
    },
    {
      id: 'my-bookings',
      title: '📋 My Bookings',
      description: 'View and manage your bookings',
      icon: <Assignment sx={{ fontSize: 40 }} />,
      isLocked: !user.canRentCars && !user.canHireDrivers,
      lockReason: 'Complete verification to view bookings',
      action: () => (user.canRentCars || user.canHireDrivers) ? navigate('/my-bookings') : setVerificationDialogOpen(true),
      buttonText: 'View Bookings',
      buttonColor: 'primary'
    }
  ];

  const getVerificationStatusColor = () => {
    switch (user.verificationStatus) {
      case VerificationStatus.APPROVED: return 'success';
      case VerificationStatus.PENDING_REVIEW: return 'warning';
      case VerificationStatus.REJECTED: return 'error';
      case VerificationStatus.DRAFT: return 'info';
      default: return 'default';
    }
  };

  const getVerificationStatusMessage = () => {
    switch (user.verificationStatus) {
      case VerificationStatus.APPROVED:
        return 'Your account is fully verified and ready to use!';
      case VerificationStatus.PENDING_REVIEW:
        return 'Your verification is under review. We\'ll notify you once approved.';
      case VerificationStatus.REJECTED:
        return 'Your verification was rejected. Please resubmit your documents.';
      case VerificationStatus.DRAFT:
        return 'Complete your verification to unlock all features.';
      default:
        return 'Complete verification to start using PolarRide services.';
    }
  };

  const startVerification = () => {
    setVerificationDialogOpen(false);
    navigate('/verification/driver', { 
      state: { 
        purpose: 'customer_verification',
        returnUrl: '/dashboard'
      }
    });
  };

  const startDriverApplication = () => {
    setDriverApplicationOpen(false);
    navigate('/verification/driver', { 
      state: { 
        purpose: 'driver_application',
        returnUrl: '/dashboard'
      }
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Welcome, {user.fullName}! 👋
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your personal mobility hub
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/notifications')}>
            <Badge badgeContent={3} color="error">
              <Notifications />
            </Badge>
          </IconButton>
          <IconButton onClick={() => navigate('/settings')}>
            <Settings />
          </IconButton>
          <Avatar sx={{ bgcolor: 'primary.main', cursor: 'pointer' }} onClick={() => navigate('/profile')}>
            {user.fullName.charAt(0)}
          </Avatar>
        </Box>
      </Box>

      {/* Verification Status Alert */}
      {isUnverified && (
        <Alert 
          severity={getVerificationStatusColor() as any} 
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => setVerificationDialogOpen(true)}
              startIcon={<Security />}
            >
              {user.verificationStatus === VerificationStatus.NONE ? 'Start Verification' : 'Continue Verification'}
            </Button>
          }
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" gutterBottom>
            🚨 Verification Required
          </Typography>
          <Typography variant="body2">
            {getVerificationStatusMessage()}
          </Typography>
          
          {user.profileCompletionPercentage > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption">Verification Progress</Typography>
                <Typography variant="caption">{user.profileCompletionPercentage}%</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={user.profileCompletionPercentage} 
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          )}
        </Alert>
      )}

      {/* User Status Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                {user.isVerified ? (
                  <VerifiedUser sx={{ fontSize: 48, color: 'success.main' }} />
                ) : (
                  <Schedule sx={{ fontSize: 48, color: 'warning.main' }} />
                )}
              </Box>
              <Typography variant="h6" gutterBottom>
                Account Status
              </Typography>
              <Chip 
                label={user.isVerified ? 'Verified' : 'Pending Verification'}
                color={user.isVerified ? 'success' : 'warning'}
                variant="outlined"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Profile Completion
              </Typography>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Typography variant="h4" color="primary">
                  {user.profileCompletionPercentage}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Security sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                User Type
              </Typography>
              <Chip 
                label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                color="primary"
                variant="outlined"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Dashboard Sections */}
      <Grid container spacing={3}>
        {dashboardSections.map((section) => (
          <Grid item xs={12} md={6} key={section.id}>
            <Card sx={{ 
              height: '100%',
              border: section.isLocked ? '1px solid' : 'none',
              borderColor: section.isLocked ? 'warning.main' : 'transparent',
              opacity: section.isLocked ? 0.7 : 1
            }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ mr: 2, position: 'relative' }}>
                    {section.icon}
                    {section.isLocked && (
                      <Lock sx={{ 
                        position: 'absolute', 
                        top: -5, 
                        right: -5, 
                        fontSize: 20, 
                        color: 'warning.main',
                        bgcolor: 'background.paper',
                        borderRadius: '50%'
                      }} />
                    )}
                  </Box>
                  <Typography variant="h6" component="h3">
                    {section.title}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                  {section.description}
                </Typography>

                {section.isLocked && section.lockReason && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="caption">
                      {section.lockReason}
                    </Typography>
                  </Alert>
                )}

                <Button
                  variant={section.isLocked ? 'outlined' : 'contained'}
                  color={section.buttonColor || 'primary'}
                  onClick={section.action}
                  startIcon={section.isLocked ? <Lock /> : <CheckCircle />}
                  fullWidth
                >
                  {section.buttonText}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            ⚡ Quick Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Help />}
                onClick={() => navigate('/support')}
              >
                Get Help
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Settings />}
                onClick={() => navigate('/settings')}
              >
                Settings
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Assignment />}
                onClick={() => navigate('/profile')}
              >
                Edit Profile
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Notifications />}
                onClick={() => navigate('/notifications')}
              >
                Notifications
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Verification Dialog */}
      <Dialog open={verificationDialogOpen} onClose={() => setVerificationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security color="primary" />
            Complete Your Verification
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            To unlock all PolarRide features, you need to complete your verification process.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This includes identity verification and document submission to ensure platform safety.
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color={user.emailVerified ? 'success' : 'disabled'} />
              </ListItemIcon>
              <ListItemText primary="Email Verification" secondary={user.emailVerified ? 'Complete' : 'Required'} />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color={user.phoneVerified ? 'success' : 'disabled'} />
              </ListItemIcon>
              <ListItemText primary="Phone Verification" secondary={user.phoneVerified ? 'Complete' : 'Required'} />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color={user.documentsSubmitted ? 'success' : 'disabled'} />
              </ListItemIcon>
              <ListItemText primary="Document Submission" secondary={user.documentsSubmitted ? 'Complete' : 'Required'} />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerificationDialogOpen(false)}>
            Later
          </Button>
          <Button variant="contained" onClick={startVerification} startIcon={<VerifiedUser />}>
            Start Verification
          </Button>
        </DialogActions>
      </Dialog>

      {/* Driver Application Dialog */}
      <Dialog open={driverApplicationOpen} onClose={() => setDriverApplicationOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonetizationOn color="secondary" />
            Become a PolarRide Driver
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Join our driver network and start earning money by providing transportation services.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Driver applications require additional verification including driving record and background check.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Requirements:</strong>
              <br />• Valid driver's license
              <br />• Clean driving record
              <br />• Background check clearance
              <br />• Vehicle inspection (if providing vehicle)
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDriverApplicationOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={startDriverApplication} startIcon={<Person />}>
            Apply Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};